import 'twin.macro';
import React, { useEffect, useRef } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { v4 as uuid } from 'uuid';
import { removeItemAtIndex } from '@teetsh/app/src/utils/listMethods';
import { Notation } from '@teetsh/app/src/types/notationSystem';
import { useTranslation } from 'react-i18next';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { NotationSystem } from './NotationSystem';
import { NotationSystemData } from './types';

interface Props {
  value: NotationSystemData[];
  onChange: (value: NotationSystemData[]) => void;
  defaultNotationsValues: Notation[];
}

export function NotationSystems({
  value,
  onChange,
  defaultNotationsValues,
}: Props) {
  const { t } = useTranslation(['userSettings', 'common']);
  const lastAddedIdRef = useRef<string | null>(null);
  const notationSystemRefs = useRef<{ [key: string]: HTMLDivElement | null }>(
    {},
  );

  useEffect(() => {
    if (
      lastAddedIdRef.current &&
      notationSystemRefs.current[lastAddedIdRef.current]
    ) {
      notationSystemRefs.current[lastAddedIdRef.current]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      lastAddedIdRef.current = null;
    }
  }, [value]);

  return (
    <>
      <Button
        tw="mb-4"
        outlined
        icon={PlusIcon}
        onClick={() => {
          const newId = uuid();
          lastAddedIdRef.current = newId;
          onChange([
            ...value,
            {
              id: newId,
              name: t(
                'userSettings:notationSystems.newNotationSystemDefaultName',
              ),
              type: 'competency',
              values: defaultNotationsValues,
            },
          ]);
        }}
        intent="primary"
      >
        {t('userSettings:notationSystems.addNewNotationSystem')}
      </Button>
      <div tw=" space-y-6">
        {value.map((notationSystem, index) => (
          <div
            key={notationSystem.id}
            ref={el => (notationSystemRefs.current[notationSystem.id] = el)}
          >
            <NotationSystem
              notationSystem={notationSystem}
              index={index}
              allNotationSystems={value}
              onChange={onChange}
              onDelete={() => onChange(removeItemAtIndex(value, index))}
              onDuplicate={() => {
                const newId = uuid();
                const duplicated = {
                  ...notationSystem,
                  id: newId,
                  name: `${notationSystem.name} (copie)`,
                  type: notationSystem.type,
                };
                lastAddedIdRef.current = newId;
                onChange([...value, duplicated]);
              }}
            />
          </div>
        ))}
      </div>
    </>
  );
}
